import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Crown, BookOpen, TrendingUp } from 'lucide-react';
import { getCreatorLeaderboard, getReaderLeaderboard, getCurrentWeekPrizes, getPrizeHistory } from '../services/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

function LeaderboardPage() {
  const [creatorLeaderboard, setCreatorLeaderboard] = useState([]);
  const [readerLeaderboard, setReaderLeaderboard] = useState([]);
  const [currentPrizes, setCurrentPrizes] = useState(null);
  const [prizeHistory, setPrizeHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [creatorsRes, readersRes, currentRes, historyRes] = await Promise.all([
          getCreatorLeaderboard(20),
          getReaderLeaderboard(20),
          getCurrentWeekPrizes(),
          getPrizeHistory(10)
        ]);
        setCreatorLeaderboard(creatorsRes.data);
        setReaderLeaderboard(readersRes.data);
        setCurrentPrizes(currentRes.data);
        setPrizeHistory(historyRes.data);
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
        toast.error('Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const getRankStyle = (rank) => {
    if (rank === 1) return 'rank-1';
    if (rank === 2) return 'rank-2';
    if (rank === 3) return 'rank-3';
    return '';
  };

  const getRankEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return rank;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-hot-rod border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 pt-8 page-padding" data-testid="leaderboard-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Trophy className="mx-auto text-trophy-gold mb-4" size={64} />
          <h1 className="text-5xl md:text-6xl font-teko text-gradient">Leaderboard</h1>
          <p className="text-xl text-text-secondary mt-2">Compete for weekly prizes</p>
        </div>

        {/* Prize Banner */}
        {currentPrizes && (
          <div className="bg-gradient-to-r from-hot-rod to-chrome-orange p-6 mb-12 flex flex-col md:flex-row items-center justify-between">
            <div>
              <p className="font-teko text-2xl uppercase">This Week's Prize Pool</p>
              <p className="text-sm opacity-80">
                {currentPrizes.week_start} - {currentPrizes.week_end}
              </p>
            </div>
            <div className="mt-4 md:mt-0 text-center md:text-right">
              <p className="font-mono text-5xl font-bold">${currentPrizes.prize_amount}</p>
              <p className="text-sm opacity-80">Split between top creators & readers</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="creators" className="w-full">
          <TabsList className="w-full justify-start bg-surface border border-border mb-8">
            <TabsTrigger value="creators" className="flex items-center gap-2 font-teko text-lg" data-testid="tab-creators">
              <Crown size={18} /> Top Creators
            </TabsTrigger>
            <TabsTrigger value="readers" className="flex items-center gap-2 font-teko text-lg" data-testid="tab-readers">
              <BookOpen size={18} /> Top Readers
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2 font-teko text-lg" data-testid="tab-history">
              <TrendingUp size={18} /> Prize History
            </TabsTrigger>
          </TabsList>

          {/* Creator Leaderboard */}
          <TabsContent value="creators">
            <div className="space-y-3">
              {creatorLeaderboard.map((entry) => (
                <Link
                  key={entry.user_id}
                  to={'/creator/' + entry.user_id}
                  className={'leaderboard-row ' + getRankStyle(entry.rank)}
                  data-testid={'creator-rank-' + entry.rank}
                >
                  <span className="font-teko text-3xl w-12 text-center">
                    {getRankEmoji(entry.rank)}
                  </span>
                  
                  <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-border">
                    <img 
                      src={entry.profile_picture || 'https://images.unsplash.com/photo-1649331593153-7575e5cd3c6e?w=100'} 
                      alt={entry.username}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="ml-4 flex-1">
                    <p className="font-bold text-lg">{entry.username}</p>
                    <div className="flex items-center gap-4 text-sm text-text-secondary">
                      <span>{entry.comic_count || 0} comics</span>
                      <span>{(entry.total_views || 0).toLocaleString()} views</span>
                      <span>{(entry.total_likes || 0).toLocaleString()} likes</span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="stat-number text-2xl text-hot-rod">{(entry.score || 0).toLocaleString()}</p>
                    <p className="text-xs text-text-secondary">points</p>
                  </div>
                </Link>
              ))}
            </div>
          </TabsContent>

          {/* Reader Leaderboard */}
          <TabsContent value="readers">
            <div className="space-y-3">
              {readerLeaderboard.map((entry) => (
                <div
                  key={entry.user_id}
                  className={'leaderboard-row ' + getRankStyle(entry.rank)}
                  data-testid={'reader-rank-' + entry.rank}
                >
                  <span className="font-teko text-3xl w-12 text-center">
                    {getRankEmoji(entry.rank)}
                  </span>
                  
                  <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-border">
                    <img 
                      src={entry.profile_picture || 'https://images.unsplash.com/photo-1649331593153-7575e5cd3c6e?w=100'} 
                      alt={entry.username}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="ml-4 flex-1">
                    <p className="font-bold text-lg">{entry.username}</p>
                    <p className="text-sm text-text-secondary">{entry.comics_read || 0} comics read</p>
                  </div>
                  
                  <div className="text-right">
                    <p className="stat-number text-2xl text-chrome-orange">{(entry.score || 0).toLocaleString()}</p>
                    <p className="text-xs text-text-secondary">engagement</p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Prize History */}
          <TabsContent value="history">
            {prizeHistory.length === 0 ? (
              <div className="text-center py-12 border border-border">
                <Trophy size={48} className="mx-auto text-text-secondary mb-4" />
                <p className="text-text-secondary">No prizes awarded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {prizeHistory.map((prize) => (
                  <div key={prize.id} className="card-hotrod p-4 flex items-center justify-between">
                    <div>
                      <p className="font-bold">{prize.winner_name}</p>
                      <p className="text-sm text-text-secondary">
                        {prize.category} - Week of {prize.week_start}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="stat-number text-xl text-trophy-gold">${prize.amount}</p>
                      <span className="badge-prize">{prize.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default LeaderboardPage;
